import React, { ReactNode, Ref } from 'react'
import { css, cx } from '@emotion/css'
import { CsHelpCircledIcon } from '../icons'
import { LinkButton, WithTooltip } from '../../design-system'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { bodyCss as bodyTextCss, panelTitleCss } from '../text'

import { FormattedMessage } from 'react-intl'
import { monotonic } from '../../util'

const getErrorID = monotonic()

const labelCss = css`
  ${bodyTextCss};

  margin: 0;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
`

const checkboxCss = css`
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;

  & + span {
    margin: 0;
    font-weight: normal;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  & + span:before {
    font-family: FontAwesome;
    font-weight: normal;
    font-size: 16px;
    text-align: center;
    line-height: 17px;
    color: ${theme.color.turquoise70};
    content: '\\00a0';
    background-color: ${theme.color.white};
    border: 1px solid ${theme.color.neutral50};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border-radius: 2px;
    display: inline-block;
    height: 19px;
    min-width: 19px;
    width: 19px;
    margin: 0 7px 0 0;
    cursor: pointer;
  }

  &:checked + span:before {
    content: '\\f00c';
  }

  &:hover:not(:focus) + span:before {
    border-color: ${theme.color.neutral60};
  }

  &:focus + span:before {
    border: 1px solid ${theme.color.turquoise70};
    box-shadow: 0 0 1px ${theme.color.turquoise70};
  }
`

const readOnlyCheckboxCss = cx(
  checkboxCss,
  css`
    & + span {
      cursor: default;
    }

    & + span:before {
      color: ${theme.color.neutral50};
      background-color: ${theme.color.neutral20};
      border: 1px solid ${theme.color.neutral40};
      box-shadow: none;
      color: ${theme.color.neutral40};
      cursor: default;
    }

    &:hover:not(:focus) + span:before {
      border: 1px solid ${theme.color.neutral40};
    }
  `
)

const disabledLabelCss = css`
  color: ${theme.color.neutral50};
`

/**
 * Props that aren't otherwise explained are simply passed as attributes
 * to the input element
 */
interface Props {
  readonly 'data-testid'?: string
  readonly disabled?: boolean

  /**
   * Toggles an EDITED indicator on the field
   */
  readonly edited?: boolean

  readonly id?: string

  /**
   * Will be passed as the ref to the actual input element
   */
  readonly inputRef?: Ref<HTMLInputElement>

  /**
   * Will be used as the aria-labeldby attribute of the input element.
   */
  readonly labelledBy?: string

  /**
   * Text describing the input. This will be used as the content
   * for the label element unless the renderLabelContent prop is used
   */
  readonly label: string

  readonly onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void

  /**
   * onChange event handler for the input element. It will be
   * passed the value of the input as an argument.
   */
  readonly onChange?: (boolean) => void

  /**
   * Callback triggered when the edit button is triggered. Specifying
   * this callback will also render the the edit button when the control
   * is disabled
   */
  readonly onEdit?: () => void

  readonly onFocus?: () => void
  readonly readOnly?: boolean

  /**
   * If provided this callback will render the contents of a help tooltip. Also,
   * a help icon will appear as part of the control's label that activates the
   * tooltip when hovered.
   */
  readonly renderHelpContent?: () => ReactNode

  /**
   * Can be used to render an alternate label content, which is normally an
   * unadorned span with the label property as its inner text. If this callback is
   * defined it will be passed the value of the label prop.
   */
  readonly renderLabelContent?: (label: string) => ReactNode

  readonly value?: boolean

  /**
   * When provided, this activates the error styling of the controller
   * and the text will appear as amplyfing information beneath the control.
   */
  readonly errorMessage?: string

  readonly labelContainerTestId?: string
}

export function Checkbox({
  disabled,
  edited,
  id,
  inputRef,
  label,
  labelledBy,
  onBlur,
  onEdit,
  onFocus,
  onChange,
  readOnly,
  renderHelpContent,
  renderLabelContent,
  value,
  errorMessage,
  labelContainerTestId,
  ...props
}: Props) {
  const hasValue = value || value === false

  const errorID = `${id ? id : `checkbox-${getErrorID()}`}-error-message`

  return (
    <label
      htmlFor={id}
      className={labelCss}
      data-e2e={`checkbox-label-${label}`}
    >
      <input
        aria-label={label as string}
        aria-labelledby={labelledBy}
        checked={value}
        className={readOnly || disabled ? readOnlyCheckboxCss : checkboxCss}
        data-testid={props['data-testid']}
        data-e2e={props['data-e2e'] || `checkbox-input-${label}`}
        disabled={disabled}
        id={id}
        ref={inputRef}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange && hasValue ? () => onChange(!value) : undefined}
        readOnly={readOnly}
        type="checkbox"
      />
      <span
        data-testid={labelContainerTestId}
        data-e2e={props['data-e2e'] || `checkbox-span-${label}`}
        className={disabled ? disabledLabelCss : undefined}
      >
        {renderLabelContent ? renderLabelContent(label) : label}
      </span>
      {renderHelpContent && (
        <WithTooltip renderTooltipContent={renderHelpContent} placement="right">
          {({ onMouseEnter, onMouseLeave }) => (
            <div
              className={css`
                margin-left: 8px;
                margin-top: -3px;
              `}
            >
              <CsHelpCircledIcon
                color={theme.color.neutral50}
                height={16}
                width={16}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </div>
          )}
        </WithTooltip>
      )}
      {disabled && onEdit && (
        <FormattedMessage id="action_edit">
          {(linkText) => (
            <LinkButton
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onEdit()
              }}
              className={css`
                padding-top: 2px;
                margin-left: 10px;
                font-size: 12px;
              `}
            >
              {linkText}
            </LinkButton>
          )}
        </FormattedMessage>
      )}
      {!disabled && edited && (
        <FormattedMessage id="detail_fieldEdited">
          {(text) => (
            <div
              className={css`
                ${panelTitleCss};
                margin-bottom: -4px;
                margin-left: 10px;
              `}
            >
              {text}
            </div>
          )}
        </FormattedMessage>
      )}

      {errorMessage && (
        <div
          className={css`
            color: ${theme.color.red50};
            padding-top: 2px;
            margin-left: 10px;
            font-size: 12px;
          `}
          id={errorID}
        >
          {errorMessage}
        </div>
      )}
    </label>
  )
}
